import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpBackend } from '@angular/common/http';
import { LogLevel } from '../utils/log/log-level.enum';
import { environment } from '../../environments/environment';
import { AppConfigService } from './app-config.service';

export interface LogEntry {
  level: LogLevel;
  type: string;
  message: string;
  timestamp: string;
}

@Injectable({
  providedIn: 'root'
})
export class LoggerService implements OnDestroy {
  // Valores por defecto como salvaguarda
  private currentLogLevel: LogLevel = LogLevel.DEBUG; 
  private tipo: string = 'LOG';
  
  private destination: string = 'console';
  private batchSize: number = 20;
  private flushIntervalMs: number = 5000;
  
  private logBuffer: LogEntry[] = [];
  private flushIntervalId: any;
  private httpClient: HttpClient;

  constructor(private httpBackend: HttpBackend, private appConfig: AppConfigService) {
    // Usamos HttpBackend directamente para evitar CUALQUIER HttpInterceptor.
    this.httpClient = new HttpClient(this.httpBackend);

    // Carga de configuración dinámica (E2DB)
    const dbLogLevel = this.appConfig.get('logLevel');
    if (dbLogLevel && (LogLevel as any)[dbLogLevel] !== undefined) {
      this.currentLogLevel = (LogLevel as any)[dbLogLevel];
    }

    this.destination = this.appConfig.get('logDestination') || 'console';
    this.batchSize = Number(this.appConfig.get('logBatchSize')) || 20;
    this.flushIntervalMs = Number(this.appConfig.get('logFlushInterval')) || 5000;

    if (this.destination === 'server' || this.destination === 'both') {
      this.startPeriodicFlush();
    }
  }

  setLogLevel(level: LogLevel): void {
    this.currentLogLevel = level;
  }

  log(levelOrMessage: LogLevel | any, ...optionalParams: any[]): void {
    let level: LogLevel;
    let params: any[];

    if (typeof levelOrMessage === 'number') {
      level = levelOrMessage as LogLevel;
      params = optionalParams;
    } else {
      level = LogLevel.OFF;
      params = [levelOrMessage, ...optionalParams];
    }

    if (level <= this.currentLogLevel) {
      // 1. Siempre renderizamos en consola (Navegador lo pinta interactivo)
      this.printToConsole(level, params);

      // 2. Si el destino incluye 'server', encolamos
      if ((this.destination === 'server' || this.destination === 'both') && level !== LogLevel.OFF) {
        this.enqueueLog(level, params);
      }
    }
  }

  private printToConsole(level: LogLevel, params: any[]): void {
    // FIX UX/DX: Al usar ...params separados, Chrome/Edge permiten expandir el objeto con el ratón
    switch (level) {
      case LogLevel.ERROR: console.error(this.tipo, ...params); break;
      case LogLevel.WARN:  console.warn(this.tipo, ...params); break;
      case LogLevel.INFO:  console.info(this.tipo, ...params); break;
      case LogLevel.DEBUG: console.debug(this.tipo, ...params); break;
      default: console.log(this.tipo, ...params); break;
    }
  }

  private enqueueLog(level: LogLevel, params: any[]): void {
    // FIX Backend Serialization: Convertimos inteligentemente los objetos a string para viajar por HTTP
    const messageParts = params.map(p => {
      if (p instanceof Error) {
        return p.message + (p.stack ? '\n' + p.stack : '');
      }
      if (typeof p === 'object' && p !== null) {
        try {
          return JSON.stringify(p);
        } catch (e) {
          return '[Objeto no serializable]';
        }
      }
      return String(p);
    });

    const message = messageParts.join(' ');

    this.logBuffer.push({
      level,
      type: this.tipo,
      message,
      timestamp: new Date().toISOString()
    });

    // Si llenamos el buffer, forzamos el envío inmediato
    if (this.logBuffer.length >= this.batchSize) {
      this.flushLogs();
    }
  }

  private startPeriodicFlush(): void {
    this.flushIntervalId = setInterval(() => {
      this.flushLogs();
    }, this.flushIntervalMs);
  }

  private flushLogs(): void {
    try{
      if (this.logBuffer.length === 0) return;

      const payload = [...this.logBuffer];
      this.logBuffer = []; // Limpiamos el buffer inmediatamente para no bloquear nuevos logs

      this.httpClient.post(environment.api_service_logs, payload).subscribe({
        next: () => {}, // Silencioso en caso de éxito
        error: (err) => {
          // Si falla el envío al servidor, no hacemos log recursivo. Solo console.error nativo
          console.error('Error enviando logs al servidor', err);
        }
      });
    } catch (e) {
      console.error('Error en flushLogs', e);
    }
  }

  // FIX: Se pasan los parámetros separados a la función this.log en todos los niveles
  error(message: string, error?: any): void {
    this.tipo = 'ERROR';
    if (error !== undefined) {
      this.log(LogLevel.ERROR, message, error);
    } else {
      this.log(LogLevel.ERROR, message);
    }
  }

  warn(message: string, meta?: any): void {
    this.tipo = 'WARN';
    if (meta !== undefined) {
      this.log(LogLevel.WARN, message, meta);
    } else {
      this.log(LogLevel.WARN, message);
    }
  }

  info(message: string, meta?: any): void {
    this.tipo = 'INFO';
    if (meta !== undefined) {
      this.log(LogLevel.INFO, message, meta);
    } else {
      this.log(LogLevel.INFO, message);
    }
  }

  debug(message: string, meta?: any): void {
    this.tipo = 'DEBUG';
    if (meta !== undefined) {
      this.log(LogLevel.DEBUG, message, meta);
    } else {
      this.log(LogLevel.DEBUG, message);
    }
  }

  serializeMessage(object: any, message?: string): void {
    this.tipo = 'LOG';
    if (message) {
      this.log(LogLevel.INFO, message, object);
    } else {
      this.log(LogLevel.INFO, 'Serialized Object:', object);
    }
  }

  ngOnDestroy(): void {
    if (this.flushIntervalId) {
      clearInterval(this.flushIntervalId);
    }
    this.flushLogs(); 
  }
}